import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DEALERSHIP_INFO, LOGO_URL } from '../config/dealership';

const Footer = () => {
  return (
    <footer
      className="relative bg-[#0A0F14] border-t border-white/5"
      data-testid="main-footer"
      id="contact"
    >
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Link to="/" className="inline-block mb-4" data-testid="footer-logo-link">
                <img 
                  src={LOGO_URL}
                  alt="CMAC & Son Autos"
                  className="h-14 w-auto"
                  data-testid="footer-logo"
                />
              </Link>
              <p className="font-sans text-[#94A3B8] text-sm leading-relaxed">
                Your trusted partner in finding the perfect pre-owned vehicle. 
                Quality, integrity, and exceptional service.
              </p>
            </motion.div>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h4 className="text-xs uppercase tracking-[0.2em] text-[#48CCCD] font-sans font-medium mb-5">
                Contact
              </h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3" data-testid="footer-contact-phone">
                  <div className="p-2 bg-[#16202A]">
                    <Phone size={14} className="text-[#48CCCD]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-1">Phone</p>
                    <a 
                      href={DEALERSHIP_INFO.phoneHref} 
                      className="text-sm text-[#F8FAFC] font-sans hover:text-[#48CCCD] transition-colors"
                    >
                      {DEALERSHIP_INFO.phone}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-3" data-testid="footer-contact-email">
                  <div className="p-2 bg-[#16202A]">
                    <Mail size={14} className="text-[#48CCCD]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-1">Email</p>
                    <a 
                      href={DEALERSHIP_INFO.emailHref}
                      className="text-sm text-[#F8FAFC] font-sans hover:text-[#48CCCD] transition-colors break-all"
                    >
                      {DEALERSHIP_INFO.email}
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Address */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h4 className="text-xs uppercase tracking-[0.2em] text-[#48CCCD] font-sans font-medium mb-5">
                Location
              </h4>
              <div className="flex items-start gap-3" data-testid="footer-contact-location">
                <div className="p-2 bg-[#16202A]">
                  <MapPin size={14} className="text-[#48CCCD]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-sans mb-1">Address</p>
                  <p className="text-sm text-[#F8FAFC] font-sans leading-relaxed">
                    {DEALERSHIP_INFO.address.line1}<br />
                    {DEALERSHIP_INFO.address.line2}<br />
                    {DEALERSHIP_INFO.address.city}<br />
                    {DEALERSHIP_INFO.address.postcode}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Opening Hours */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h4 className="text-xs uppercase tracking-[0.2em] text-[#48CCCD] font-sans font-medium mb-5">
                Opening Hours
              </h4>
              <div className="flex items-start gap-3" data-testid="footer-contact-hours">
                <div className="p-2 bg-[#16202A]">
                  <Clock size={14} className="text-[#48CCCD]" strokeWidth={1.5} />
                </div>
                <div className="space-y-1">
                  {DEALERSHIP_INFO.hoursFormatted.map(({ day, time }) => (
                    <div key={day} className="flex justify-between gap-4 text-sm font-sans">
                      <span className="text-[#94A3B8]">{day.slice(0, 3)}</span>
                      <span className={time === 'Closed' ? 'text-[#94A3B8]' : 'text-[#F8FAFC]'}>{time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-[#94A3B8] font-sans">
              {DEALERSHIP_INFO.copyright}
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/privacy-policy"
                className="text-xs text-[#94A3B8] hover:text-[#48CCCD] transition-colors font-sans"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-xs text-[#94A3B8] hover:text-[#48CCCD] transition-colors font-sans"
                data-testid="footer-terms-link"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
